import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { LogicielCaisseProduitService } from './logiciel-caisse-produit.service';

/**
 * Synchronise les produits ERP vers le logiciel caisse après création manuelle
 * (même canal CSV upsert que l'import XLSX affichages).
 * Best-effort : n'annule jamais le succès ERP.
 */
@Injectable({ providedIn: 'root' })
export class LogicielCaisseProductSyncService {
  constructor(
    private readonly lcAuth: LogicielCaisseAuthService,
    private readonly lcProduit: LogicielCaisseProduitService
  ) {}

  syncProductAfterErpCreate(product: Record<string, unknown>): Observable<boolean> {
    return this.syncProductsAfterErpCreate([product]);
  }

  syncProductsAfterErpCreate(products: Record<string, unknown>[]): Observable<boolean> {
    const csv = this.buildImportCsv(products || []);
    if (!csv) {
      console.warn(
        '[LC Product Sync] rien à synchroniser (nom / code-barres manquants — parents exclus)'
      );
      return of(false);
    }

    return this.lcAuth.bootstrap({ forceRefresh: true }).pipe(
      switchMap((status) => {
        const ready = status === 'ready' && this.lcAuth.isAuthenticated();
        if (ready) {
          return of(true);
        }
        return this.lcAuth.ensureAuthenticatedSession();
      }),
      switchMap((ok) => {
        if (!ok && !this.lcAuth.isAuthenticated()) {
          console.warn(
            '[LC Product Sync] session LC absente — produit ERP OK, sync caisse ignorée'
          );
          return of(false);
        }
        return this.lcProduit.importProduitsCsv(csv).pipe(
          map((r) => {
            const created = r.created ?? 0;
            const updated = r.updated ?? 0;
            const ok = created + updated > 0;
            if (ok) {
              console.log(
                '%c[LC Product Sync] SUCCÈS — produit(s) dans logiciel caisse',
                'color:#0a7;font-weight:bold',
                { created, updated, message: r.message }
              );
            } else {
              console.error(
                '%c[LC Product Sync] ÉCHEC — produit NON ajouté dans logiciel caisse',
                'color:#c00;font-weight:bold',
                { created, updated, failed: r.failed, errors: r.errors, message: r.message }
              );
            }
            return ok;
          }),
          catchError((err) => {
            console.error(
              '%c[LC Product Sync] ÉCHEC HTTP — produit NON ajouté dans logiciel caisse',
              'color:#c00;font-weight:bold',
              err
            );
            return of(false);
          })
        );
      }),
      catchError((err) => {
        console.error(
          '%c[LC Product Sync] ÉCHEC AUTH — produit NON ajouté dans logiciel caisse',
          'color:#c00;font-weight:bold',
          err
        );
        return of(false);
      })
    );
  }

  /** CSV `;` attendu par `POST /api/products/import/csv`. */
  private buildImportCsv(products: Record<string, unknown>[]): string {
    const escapeCsv = (v: string) => {
      const s = String(v ?? '');
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const headers = [
      'NOM',
      'REF INTERNE',
      'REF FABRICANT',
      'CODE BARRE',
      'PRIX VENTE',
      'PRIX ACHAT',
      'FAMILLE',
      'SOUS FAMILLE',
      'COULEUR',
      'TAILLE',
      'RAYON',
      'CODE ARTICLE',
      'ACTIF',
      ...Array.from({ length: 12 }, (_, i) => `PARAM${i + 1}`)
    ];

    const lines: string[] = [];
    for (const p of products) {
      const cells = this.mapErpProductToCsvCells(p);
      if (!cells) {
        continue;
      }
      lines.push(cells.map(escapeCsv).join(';'));
    }

    if (!lines.length) {
      return '';
    }
    return [headers.join(';'), ...lines].join('\n');
  }

  private mapErpProductToCsvCells(p: Record<string, unknown>): string[] | null {
    const str = (v: unknown) => (v == null ? '' : String(v).trim());
    const numStr = (v: unknown) => {
      if (v == null || v === '') {
        return '';
      }
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
      return Number.isFinite(n) ? String(n) : '';
    };

    // Même règle que l'import XLSX LC : variante avec nom + code-barres.
    const nom = str(p['nom']);
    const codeBarre = str(p['code_barre']);
    if (!nom || !codeBarre) {
      return null;
    }

    const isFamily =
      p['is_family'] === true ||
      p['is_family'] === 1 ||
      p['is_family'] === '1' ||
      String(p['type'] || '').toLowerCase() === 'gamme' ||
      String(p['type'] || '').toLowerCase() === 'famille';
    if (isFamily) {
      return null;
    }

    const params = Array.from({ length: 12 }, (_, i) => str(p[`param${i + 1}`]));
    const actifRaw = p['actif'] ?? p['is_active'] ?? true;
    const actif =
      actifRaw === false || actifRaw === 0 || actifRaw === '0' || actifRaw === 'false'
        ? '0'
        : '1';

    return [
      nom,
      str(p['ref_interne']) || str(p['reference_interne']),
      str(p['ref_fabricant']) || str(p['reference_fabricant']),
      codeBarre,
      numStr(p['prix_unitaires']) ||
        numStr(p['result_ttc']) ||
        numStr(p['prix_vente']) ||
        numStr(p['prix']) ||
        '',
      numStr(p['prix_achat']) || numStr(p['result_ht']) || '',
      str(p['famille']) || str(p['categorie']) || str(p['categorie_libelle']),
      str(p['sous_famille']) || str(p['sous_categorie']) || str(p['sous_categorie_libelle']),
      str(p['couleur']),
      str(p['taille']),
      str(p['rayon']),
      str(p['code_art']) || str(p['code_article']),
      actif,
      ...params
    ];
  }
}
